import type { ComponentType } from 'react';
import { BillingPanel } from './modules/billing/BillingPanel.js';
import { SalesPanel } from './modules/sales/SalesPanel.js';
import { TicketsPanel } from './modules/tickets/TicketsPanel.js';
import { MyTicketsPanel } from './modules/tickets/MyTicketsPanel.js';
import { ErpPanel } from './modules/erp/ErpPanel.js';
import { RetentionPanel } from './modules/retention/RetentionPanel.js';

/**
 * O início da interna, como moldura de painéis.
 *
 * ## Por que "diretoria" é tela e não módulo
 *
 * O `DashboardDiretoria` de hoje lê tabelas de cobrança, comercial e gestão
 * direto — é um módulo que sabe do banco dos outros, que é exatamente o que a
 * fronteira de módulo proíbe. Aqui cada módulo **publica os próprios números
 * pelo próprio contrato** e registra o painel nesta lista. Ninguém lê tabela
 * alheia, e o início continua sendo uma página só.
 *
 * ## Nenhum módulo edita a HomeScreen
 *
 * Painel novo é uma linha aqui. Se o início de um perfil precisar de moldura
 * diferente, isso é variação declarada neste arquivo — não um `if` dentro da
 * tela, que é como uma tela vira o lugar onde sete módulos se encontram.
 *
 * ## O recurso é conferido
 *
 * Um `recurso` com typo faz o painel sumir para todo mundo sem erro em lugar
 * nenhum: `canAccess` nega por omissão, corretamente. `registry.test.ts`
 * compara esta lista com o catálogo da API, como já faz com o `menu.ts`.
 *
 * ## O lugar é declarado aqui, e não decidido na tela
 *
 * Desde 29/09/2026 o início tem três faixas: a fileira de números logo abaixo
 * da saudação (`numbers`), os cartões lado a lado (`column`) e o resto, abaixo
 * (`strip`). Quem diz em qual faixa um painel mora é esta lista — a tela só
 * filtra por `placement`, e continua sem saber o nome de módulo nenhum.
 */
export type HomePanelPlacement = 'numbers' | 'column' | 'strip';

/**
 * As abas do início, na ordem em que aparecem — desde 01/10/2026.
 *
 * Até ali os painéis vinham todos empilhados numa página só: demandas,
 * comercial, ERP, retenção e cobrança, um embaixo do outro. Quem tinha
 * permissão em tudo rolava cinco seções para achar a sua. Agora cada módulo é
 * uma aba, e a aba só existe se a pessoa pode ver ao menos um painel dela — a
 * mesma cascata do menu: aba sem painel não desenha rótulo órfão.
 *
 * A chave viaja no endereço (`/?panel=erp`), e por isso é exportada: a
 * rota do início valida a busca contra esta lista. Ausente, abre a primeira
 * aba que a pessoa vê.
 */
export const HOME_TABS = [
  { key: 'demandas', label: 'Tickets' },
  { key: 'comercial', label: 'Comercial' },
  { key: 'erp', label: 'ERP' },
  { key: 'retencao', label: 'Retenção' },
  { key: 'cobranca', label: 'Cobrança' },
] as const;

export type HomeTab = (typeof HOME_TABS)[number]['key'];

export const HOME_TAB_KEYS = HOME_TABS.map((t) => t.key) as [HomeTab, ...HomeTab[]];

export interface HomePanel {
  /** identificador estável — some do início se o módulo sair */
  key: string;
  /** a aba do início em que o painel mora — ver `HOME_TABS` */
  tab: HomeTab;
  component: ComponentType;
  placement: HomePanelPlacement;
  /** quem não pode ver não vê o painel. Sem recurso, aparece para todo mundo. */
  resource?: string;
  action?: string;
}

export const PANELS: HomePanel[] = [
  {
    key: 'demandas',
    tab: 'demandas',
    placement: 'numbers',
    component: TicketsPanel,
    resource: 'tickets.tickets',
    action: 'ver',
  },
  /*
   * O ERP publica os próprios números pelo próprio contrato — os mesmos de
   * `GET /erp/health` que a tela de saúde lê, pela mesma chave de consulta.
   *
   * Sem esta linha a moldura nasce vazia para quem não atende demandas, e a
   * pressão seguinte é um módulo de relatórios lendo tabela alheia: exatamente
   * o que "diretoria é tela, não módulo" existe para não deixar acontecer.
   */
  {
    key: 'erp',
    tab: 'erp',
    placement: 'strip',
    component: ErpPanel,
    resource: 'erp.mirror',
    action: 'ver',
  },
  /*
   * Os números da retenção saem de `GET /retention/numbers`, que é do módulo e
   * fica sob `retention.requests:ver` — a mesma permissão do quadro, porque
   * a contagem é a mesma informação agregada.
   *
   * `sem_quadro` é a resposta da base que ainda não tem fase cadastrada — o
   * estado em que toda praça nasce, porque as fases vêm da extração de
   * `retencao-01` e não do seed. O painel diz isso com essas palavras em vez de
   * desenhar zeros: zero e "não há quadro" são respostas diferentes, e a segunda
   * manda configurar, não procurar defeito.
   */
  {
    key: 'retencao',
    tab: 'retencao',
    placement: 'strip',
    component: RetentionPanel,
    resource: 'retention.requests',
    action: 'ver',
  },
  /*
   * Os números da cobrança saem de `GET /billing/numbers`, e o recurso é o da
   * MESA — não um `cobranca.painel` inventado. É a permissão que o controller
   * exige naquela rota, e é a decisão escrita no catálogo do módulo: os números
   * são a mesma informação da mesa, agregada. Quem não pode ver os atendimentos
   * da base não pode ver a contagem deles — e uma chave que o catálogo não
   * declara faria o painel sumir para todo mundo sem erro em lugar nenhum.
   *
   * `sem_coleta` é a resposta da base em que a coleta nunca rodou — o estado em
   * que toda praça nasce, porque o espelho do ERP só é preenchido pela esteira.
   * E note que ela NÃO é deduzida de `boletos` estar vazia: quem
   * responde é o evento `cobranca_coleta/concluida`, porque "não há boleto em
   * aberto" e "ninguém coletou" são fatos diferentes. O painel diz isso com
   * essas palavras em vez de desenhar R$ 0,00 — só a segunda manda procurar a
   * coleta.
   */
  {
    key: 'cobranca',
    tab: 'cobranca',
    placement: 'strip',
    component: BillingPanel,
    resource: 'billing.desk',
    action: 'ver',
  },
  /*
   * Os números do comercial saem de `GET /sales/numbers`, e o recurso é o
   * das VENDAS — não um `comercial.painel` inventado, e não o das comissões.
   *
   * É a permissão que o controller exige naquela rota, e é a decisão escrita no
   * catálogo do módulo: os números são as vendas da competência, agregadas. E o
   * payload daquela rota **não carrega comissão nenhuma**, de propósito: um
   * cartão que somasse as duas coisas faria a permissão de ver vendas vazar a de
   * ver o custo de remuneração da base, sem que a concessão tivesse dito isso em
   * lugar nenhum.
   *
   * `app.sales_sales` nasce vazia em toda praça, e nesse estado o painel
   * responde `sem_vendas` em vez de desenhar zero — "nenhuma venda nesta base, em
   * competência nenhuma" e "esta competência está zerada" exigem ações opostas: a
   * primeira é importar, a segunda é uma resposta sobre o mês.
   *
   * O painel do CONSULTOR (`comercial-28`) não está aqui: ele depende do escopo
   * de carteira de `comercial-21`, que não existe — e um painel "só os seus
   * números" sobre uma consulta que devolve a base inteira seria a tela afirmando
   * um recorte que o servidor não faz.
   */
  {
    key: 'comercial',
    tab: 'comercial',
    placement: 'column',
    component: SalesPanel,
    resource: 'sales.sales',
    action: 'ver',
  },
  /*
   * O "Comigo" do painel de números, aberto em linhas — mesma rota da mesa e
   * mesmo recurso, porque é a mesma informação com outra forma.
   */
  {
    key: 'demandas-comigo',
    tab: 'demandas',
    placement: 'column',
    component: MyTicketsPanel,
    resource: 'tickets.tickets',
    action: 'ver',
  },
];
